import Cookies from 'js-cookie';
import dayjs from 'dayjs';
import type { Dispatch } from 'react';
import {
  setUser,
  getUserAndTokenFromStorage,
  hasPermission,
  backfillCdnToken,
  updateUser as updateUserStore,
} from '@firsttable/functions';
import type { BackfilledAuth } from '@firsttable/functions';
import { User } from '@firsttable/types';
import { initializeApollo } from '@/lib/apollo';
import Logger from '@/lib/logger';

const logger = new Logger('Auth actions');

/* eslint-disable-next-line */
type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
        error?: string;
      };
};

// TypeScript enums occupy both value and type namespaces.
// eslint-disable-next-line no-shadow
export enum AuthType {
  SUCCESS = 'LOGIN_SUCCESS',
  UPDATED = 'LOGIN_UPDATED',
  FAILURE = 'LOGIN_ERROR',
  LOGOUT = 'LOGOUT',
  REDIRECTING = 'REDIRECTING',
  GUEST = 'GUEST',
}

type UserPayload = {
  [AuthType.GUEST]: Record<string, never>;
  [AuthType.SUCCESS]: {
    user: User;
    token: string;
    cdnToken: string;
  };
  [AuthType.UPDATED]: {
    user: User;
    token: string;
    cdnToken: string;
  } | null;
  [AuthType.FAILURE]: {
    error: string;
  };
  [AuthType.REDIRECTING]: {
    user: User;
    token: string;
    cdnToken: string;
    redirect: boolean | string;
    loading: boolean;
  };
  [AuthType.LOGOUT]: Record<string, never>;
};

export type UserActions = ActionMap<UserPayload>[keyof ActionMap<UserPayload>];

type RestoredAuth = UserPayload[AuthType.SUCCESS];

export function checkUserOnMount(dispatch: Dispatch<UserActions>): RestoredAuth | null {
  const auth = getUserAndTokenFromStorage();
  if (auth.user) {
    const restoredAuth: RestoredAuth = {
      user: auth.user,
      token: auth.token,
      cdnToken: auth.cdnToken,
    };
    dispatch({ type: AuthType.SUCCESS, payload: restoredAuth });
    // '' means a previous fetch failed, so do not retry.
    if (auth.user.isLoggedIn && auth.token && auth.cdnToken == null) {
      backfillCdnToken(initializeApollo(), restoredAuth)
        .then((updatedAuth: BackfilledAuth | null) => {
          if (updatedAuth) {
            dispatch({ type: AuthType.UPDATED, payload: updatedAuth });
          }
        });
    }
    return restoredAuth;
  }
  return null;
}

export function loginGuest(dispatch) {
  dispatch({ type: AuthType.GUEST });
}

export function updateUser(dispatch, payload) {
  const auth = updateUserStore(payload);
  dispatch({ type: AuthType.UPDATED, payload: auth });
  logger.debug(payload ? 'updateUser actioned' : 'updateUser failed', payload);
}

export function loginUser(dispatch, loginPayload) {
  const data = loginPayload;
  logger.debug('loginUser payload', data);
  try {
    if (data.member) {
      const { member, token, cdnToken, redirect } = data;

      const auth = {
        user: { ...member, isLoggedIn: true },
        token,
        cdnToken,
      };
      setUser(auth);
      if (hasPermission(['ADMIN', 'STAFF', 'RESTAURANT_ADMINISTRATION'])) {
        Cookies.set('Admin', '1', { secure: false });
        Cookies.set('AdminComputer', '1', { secure: false });
      }
      Cookies.set('PastLoggedIn', '1', { expires: 365, path: '/' });

      // Unset promoCode in local storage
      localStorage.removeItem('firsttable.promoCode');
      localStorage.setItem('firsttable.memberCheckTime', dayjs().unix().toString());

      dispatch({ type: AuthType.REDIRECTING, payload: { ...auth, redirect } });

      return data;
    }
    dispatch({ type: AuthType.FAILURE, error: 'No user' });
    logger.debug('Auth Failure', AuthType.LOGOUT);
  } catch (error) {
    dispatch({ type: AuthType.FAILURE, error });
    logger.error('login error', error);
  }
}

/**
 * Handle post login redirects
 * @param dispatch
 */
export async function logout(dispatch) {
  const apolloClient = initializeApollo();
  setUser(null);
  // await apolloClient.queryManager.fetchQueryRejectFns;
  await apolloClient.clearStore();

  dispatch({ type: AuthType.LOGOUT });
  logger.debug('Logout actioned');
}
